import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React, { useState, useRef, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import useSWRInfinite from 'swr/infinite';

const RemoteDataAutoComplete = ({
  label,
  objectType,
  name,
  selectedItem,
  onSelectedItemChanged,
  placeholder,
  multiple = false,
  size: controlSize = 'small',
  serverFilter,
  pageSize = 50,
  disableClearable = false,
  fetcher,
  dedupingInterval = 10000,
  onBlur,
  onFocus,
  error: isError,
  helperText,
  required,
  iconMap,
  optionsList,
  disabled,
  extraFilter,
  autoFocus,
  hideLabel,
  renderOptionDecoratorHandler,
  inputStyle,
  limitTags,
  filterOptionsFn,
  freeSolo = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const listBoxRef = useRef(null);
  const scrollTopRef = useRef(0);

  const deferredInputValue = useDeferredValue(inputValue);

  const getKeyHandler = useCallback((pageIndex) => {
    if (!isOpen) return null;
    const nextPage = pageIndex + 1;
    const fetchKey = serverFilter
      ? `/objectType/${objectType}/?pageSize=${pageSize}&page=${nextPage}`
      : `/objectType/${objectType}/?text=${deferredInputValue}&pageSize=${pageSize}&page=${nextPage}`;
    return [
      fetchKey,
      objectType,
      pageSize,
      pageIndex + 1,
      deferredInputValue,
      extraFilter
    ];
  },
  [isOpen, serverFilter, objectType, pageSize, deferredInputValue, extraFilter]);
  const { data, error, size, setSize } = useSWRInfinite(
    getKeyHandler,
    fetcher,
    {
      dedupingInterval: dedupingInterval || 10000,
    }
  );

  useEffect(() => {
    if (listBoxRef.current) {
      listBoxRef.current.scrollTo({ top: scrollTopRef.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (serverFilter) {
      setSize(1);
    }
  }, [setSize, serverFilter]);

  const options = useMemo(() => {
    let tempOptions = [];
    if (optionsList) {
      tempOptions = optionsList;
    } else {
      tempOptions = data ? [].concat(...data) : [];
    }
    if (filterOptionsFn) {
      tempOptions = filterOptionsFn(tempOptions);
    }

    return tempOptions;
  }, [data, filterOptionsFn, optionsList]);

  const isLoadingInitialData = isOpen && !data && !error;
  const isLoadingMore = isLoadingInitialData
    || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < pageSize);

  const filterOptions = useMemo(() => {
    if (!serverFilter) {
      return undefined;
    }

    return (x) => x;
  }, [serverFilter]);

  const onInputChangeHandler = useMemo(() => {
    if (!serverFilter) {
      return undefined;
    }

    return (event, newInputValue) => {
      setInputValue(newInputValue);
    };
  }, [serverFilter]);

  const getOptionLabel = ({ id, text }) => text ?? id ?? '';
  const renderOptionHandler = useCallback((props, option) => (
    <li {...props} key={option.id}>
      {iconMap && iconMap[option.id]}
      {option.text || ' '}
    </li>
  ), [iconMap]);

  const isOptionEqualToValue = (option, value) => {
    if (!value || !option) {
      return false;
    }

    return option.id === value.id;
  };

  const onOpenHandler = useCallback(() => setIsOpen(true), []);
  const onCloseHandler = useCallback(() => setIsOpen(false), []);
  const onChangeHandler = (event, newValue) => {
    if (onSelectedItemChanged) {
      if (typeof (newValue) === 'string' && freeSolo) {
        onSelectedItemChanged({ id: newValue, text: newValue }, 'RemoteDataAutoComplete', name);
      } else {
        onSelectedItemChanged(newValue, 'RemoteDataAutoComplete', name);
      }
    }
  };

  const onBlurHandler = (event) => {
    if (selectedItem?.text !== event?.target?.value) {
      onChangeHandler(event, event?.target?.value);
    }
  };

  const loadMoreResults = () => {
    if (!isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  };

  return (
    <Autocomplete
      freeSolo={!!freeSolo}
      disableClearable={disableClearable}
      multiple={!!multiple}
      fullWidth
      open={isOpen}
      onOpen={onOpenHandler}
      onClose={onCloseHandler}
      includeInputInList
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={isLoadingMore}
      value={selectedItem}
      onChange={onChangeHandler}
      disabled={!!disabled}
      size={controlSize}
      onBlur={freeSolo ? onBlurHandler : null}
      limitTags={limitTags}
      ListboxProps={{
        onScroll: ({ target, currentTarget }) => {
          listBoxRef.current = target;
          scrollTopRef.current = target.scrollTop; // <-- save scroll position on every scroll
          const listboxNode = currentTarget;
          const threshold = 10;
          const isAtBottom = listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + threshold;
          if (isAtBottom) {
            loadMoreResults(target, currentTarget);
          }
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          size={controlSize}
          label={!hideLabel ? `${label}${required ? ' *' : ''}` : undefined}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            placeholder: `${placeholder || label}`,
            endAdornment: (
              <>
                {isLoadingMore ? (
                  <CircularProgress color="inherit" size={26} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            style: inputStyle
          }}
          name={name}
          onBlur={onBlur}
          onFocus={onFocus}
          error={!!isError}
          helperText={helperText}
        />
      )}
      renderOption={renderOptionDecoratorHandler ?? renderOptionHandler}
      filterOptions={filterOptions}
      onInputChange={onInputChangeHandler}
    />
  );
};

export default React.memo(RemoteDataAutoComplete);
